import React, { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button } from 'antd'
import { Http } from '../../../helpers/axois'
import { getRandomId } from '../../../utils/random-id'
import { useToast } from '../../../helpers/commonFunction'
import { AppState } from '../../../stateManager/store'
import { getFilename, validateFile } from './helpers'
import style from './styles/marketplace-apps.module.css'

// icons
import { ReactComponent as PlaceholderIcon } from '../../../assets/svg/thumbnailPlaceholder.svg'
import { ReactComponent as CloseIcon } from '../../../assets/svg/solo/closeBtn.svg'
import { supportedFileTypes, supportedImageTypes } from './constants'

interface IUploadThumnail {
  setState: (url: string) => void
  setType?: (type: string) => void
  url: string
  type: 'THUMBNAIL' | 'APP'
  edit?: boolean
}

export const Upload: React.FC<IUploadThumnail> = ({
  setState,
  setType,
  url,
  type,
  edit,
}) => {
  const orgId = useSelector((state: AppState) => state.org.selectedOrg?.id)
  const inputRef = useRef<HTMLInputElement>(null)
  const { SuccessToast, ErrorToast } = useToast()
  const [loading, setLoading] = useState(false)
  const orgPermissions: any = useSelector((state: any) => {
    return state.org.orgPermissions
  }, (oldPermissions: any, newPermissions: any) => JSON.stringify(oldPermissions) === JSON.stringify(newPermissions))
  const { selectedOrg } = useSelector((state: AppState) => state.org)
const loggedInUserId = useSelector((state: any) => {
    return state.user.data.user.id
})
  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target && e.target.files && e.target.files[0]

    if (!file) {
      return
    }

    const errMsg = validateFile(file, {
      type: "file",
      size: 4,
    })

    if(errMsg) {
      ErrorToast(errMsg)
      if (inputRef?.current?.value) {
        inputRef.current.value = ''
      }
      return
    }

    const name = getRandomId() + '_' + file.name
    const formData = new FormData()
    formData.append('file', file, name)

    setLoading(true)
    try {
      const { url, type } = await onUpload(formData)
      setState(url)
      if (setType && type) {
        setType(type);
      }
      SuccessToast(
        `Finished uploading ${type === 'APP' ? 'artifact' : 'image'}`
      )
    } catch (err) {
      ErrorToast(err.message)
    } finally {
      setLoading(false)
      if (inputRef?.current?.value) {
        inputRef.current.value = ''
      }
    }
  }

  const onUpload = async (formData: FormData): Promise<any> => {
    if ((!orgPermissions?.includes("marketplace.PublishApp") || !orgPermissions.includes('org.View')) && type==="APP" && loggedInUserId!=selectedOrg?.owner) {
      ErrorToast("Not Authorized: Please request owner of this organization to provide access")
      return
    }
    const endpoint =
      type === 'APP'
        ? `/orgs/${orgId}/marketplaceApps/file/upload`
        : `/orgs/${orgId}/marketplaceApps/image/upload`

    try {
      const res: any = await Http.post(endpoint, formData).toPromise()
      return res
    } catch (err) {
      var msg = err?.message || `Failed to upload ${type === 'APP' ? 'artifact' : 'image'}`
      if (err === "Forbidden") {
        msg = "Not Authorized: Please request owner of this organization to provide access"
      }
      throw new Error(msg)
    }
  }

  return (
    <div className={style.upload}>
      {type === 'APP' && (
        <App
          setState={setState}
          onChange={onChange}
          inputRef={inputRef}
          loading={loading}
          url={url}
          edit={Boolean(edit)}
        />
      )}
      {type === 'THUMBNAIL' && (
        <Thumbnail
          thumbnail={url}
          onChange={onChange}
          inputRef={inputRef}
          loading={loading}
        />
      )}
    </div>
  )
}

const Thumbnail: React.FC<{
  thumbnail: string
  loading: boolean
  inputRef: React.RefObject<HTMLInputElement>
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}> = ({ thumbnail, loading, inputRef, onChange }) => {
  return (
    <div className={style.uploadThumbnail}>
      <p>App Preview Image</p>

      <div className={style.placeholderWrapper}>
        <div className={style.placeholder}>
          {thumbnail ? (
            <img src={thumbnail} alt="App Thumbnail" />
          ) : (
            <PlaceholderIcon width={90} height={90} />
          )}
        </div>
        <Button
          type="primary"
          onClick={() => inputRef?.current?.click()}
          loading={loading}
        >
          {loading ? 'Uploading' : 'Upload'} Image
        </Button>
      </div>

      <input type="file" ref={inputRef} onChange={onChange} accept={supportedImageTypes.join(",")} />
    </div>
  )
}

const App: React.FC<{
  url: string
  loading: boolean
  inputRef: React.RefObject<HTMLInputElement>
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  setState: (url: string) => void
  edit: boolean
}> = ({ inputRef, onChange, loading, url, setState, edit }) => {
  const closeIconStyle = {
    opacity: edit ? 0.5 : 1,
    cursor: edit ? 'not-allowed' : 'pointer',
  }

  const handleClose = () => {
    if (!edit) {
      setState('')
    }
  }

  return (
    <div className={style.uploadApp}>
      <p className={style.required}>Upload Application</p>

      {url ? (
        <div className={style.artifact}>
          <p>{getFilename(url)}</p>
          <CloseIcon
            style={closeIconStyle}
            width={24}
            height={24}
            onClick={handleClose}
          />
        </div>
      ) : (
        <div className={style.uploadAppWrapper}>
          <Button
            type="primary"
            onClick={() => inputRef?.current?.click()}
            loading={loading}
          >
            Upload Application
          </Button>
        </div>
      )}

      <input type="file" ref={inputRef} onChange={onChange} accept={supportedFileTypes.join(",")}/>
    </div>
  )
}
